import React, { useEffect, useState } from 'react';
import { Modal, Button, Descriptions, Typography } from 'antd';
import { BgColorsOutlined } from '@ant-design/icons';

const { Text } = Typography;

const ShopifyCustomizationModal = ({ visible, onCancel, selectedCustomization }) => {
    const [showHiddenItems, setShowHiddenItems] = useState(false);

    // Extract customization data from properties or customAttributes
    const extractCustomizationData = (customization) => {
        const items = [];

        // Check if customization has properties (line item properties from Shopify)
        if (customization?.properties) {
            try {
                // Properties might be a JSON string or already parsed
                const props = typeof customization.properties === 'string' 
                    ? JSON.parse(customization.properties) 
                    : customization.properties;

                if (Array.isArray(props)) {
                    props.forEach(prop => {
                        if (prop.name && prop.value) {
                            // Check if value contains price information like [$1.99]
                            const priceMatch = prop.value.match(/\[\$(\d+\.?\d*)\]/);
                            const cleanValue = prop.value.replace(/\s*\[\$\d+\.?\d*\]\s*$/, '').trim();
                            
                            items.push({
                                label: prop.name,
                                value: cleanValue || prop.value,
                                priceDelta: priceMatch ? parseFloat(priceMatch[1]) : 0
                            });
                        }
                    });
                }
            } catch (error) {
                console.error('Error parsing properties:', error);
            }
        }

        // Fallback to customAttributes if properties is empty
        if (items.length === 0 && customization?.customAttributes) {
            try {
                const attrs = typeof customization.customAttributes === 'string'
                    ? JSON.parse(customization.customAttributes)
                    : customization.customAttributes;

                if (Array.isArray(attrs)) {
                    attrs.forEach(attr => {
                        if (attr.name && attr.value) {
                            // Similar price extraction logic
                            const priceMatch = attr.value.match(/\[\$(\d+\.?\d*)\]/);
                            const cleanValue = attr.value.replace(/\s*\[\$\d+\.?\d*\]\s*$/, '').trim();
                            
                            items.push({
                                label: attr.name,
                                value: cleanValue || attr.value,
                                priceDelta: priceMatch ? parseFloat(priceMatch[1]) : 0
                            });
                        }
                    });
                }
            } catch (error) {
                console.error('Error parsing customAttributes:', error);
            }
        }

        return items;
    };

    const customizationItems = selectedCustomization ? extractCustomizationData(selectedCustomization) : [];
    const isVisibleItem = (item) => {
        const label = item.label?.trim() || '';
        const normalizedLabel = label.toLowerCase();

        if (!label.startsWith('_')) {
            return true;
        }

        return normalizedLabel === '_customily-production-url'
            || normalizedLabel.startsWith('_customize photo');
    };

    const validItems = customizationItems.filter(isVisibleItem);
    const hiddenItems = customizationItems.filter(item => !isVisibleItem(item));
    const itemsToRender = showHiddenItems ? customizationItems : validItems;

    useEffect(() => {
        setShowHiddenItems(false);
    }, [selectedCustomization]);

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <BgColorsOutlined style={{ color: '#1890ff' }} />
                    <span>Customer Customization</span>
                </div>
            }
            open={visible}
            onCancel={onCancel}
            footer={[
                <Button key="close" type="primary" onClick={onCancel}>
                    Close
                </Button>
            ]}
            width={800}
            styles={{
                body: {
                    padding: '24px'
                }
            }}
        >
            {customizationItems.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                    <BgColorsOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                    <div>No customization details available</div>
                </div>
            ) : (
                <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    <Descriptions
                        bordered
                        column={1}
                        size="middle"
                        styles={{
                            label: {
                                width: '250px',
                                backgroundColor: '#f8f9fa',
                                fontWeight: 500,
                                fontSize: '14px'
                            },
                            content: {
                                backgroundColor: '#fff',
                                padding: '16px',
                                fontSize: '14px'
                            }
                        }}
                    >
                        {itemsToRender.map((item, idx) => (
                            <Descriptions.Item key={idx} label={item.label}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <Text strong style={{ fontSize: '14px' }}>{item.value}</Text>
                                    {item.priceDelta !== 0 && (
                                        <Text type="secondary" style={{ fontSize: '12px', fontStyle: 'italic' }}>
                                            (+${item.priceDelta.toFixed(2)})
                                        </Text>
                                    )}
                                </div>
                            </Descriptions.Item>
                        ))}
                    </Descriptions>

                    {hiddenItems.length > 0 && (
                        <Button
                            type="link"
                            block
                            onClick={() => setShowHiddenItems(current => !current)}
                            style={{ marginTop: 12 }}
                        >
                            {showHiddenItems
                                ? 'Hide other customization fields'
                                : `Show ${hiddenItems.length} more customization field${hiddenItems.length > 1 ? 's' : ''}`}
                        </Button>
                    )}
                </div>
            )}
        </Modal>
    );
};

export default ShopifyCustomizationModal;
